import { useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/config";
import { useLang } from "@/i18n/LanguageContext";
import QuoteForm from "@/components/QuoteForm";
import Prime49 from "@/components/Prime49";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Phone, Star, CreditCard, Smartphone, Zap, ShieldCheck, Store,
  Menu, X, ArrowRight, Check, DollarSign, Truck, Wallet, Ban,
  HandCoins, Church, GraduationCap, Building2, HeartHandshake,
  Monitor, LayoutGrid,
} from "lucide-react";

const MACHINE_IMG = "https://images.unsplash.com/photo-1647427017067-8f33ccbae493?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CHECKOUT_BG = "https://images.unsplash.com/photo-1556740714-a8395b3bf30f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const YT_ID = "2prWwL0mrcQ";

const NAV_KEYS = [
  { key: "offer", href: "#offer" },
  { key: "terminal", href: "#machine" },
  { key: "prime49", href: "#prime49" },
  { key: "givehub", href: "/givehub" },
  { key: "why", href: "#why" },
  { key: "faq", href: "#faq" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const VIEWPORT_ONCE = { once: true };

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <motion.div
        data-testid="scroll-progress"
        style={{ scaleX }}
        className="fixed left-0 right-0 top-0 z-[60] h-1 origin-left bg-primary"
      />
      <Nav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Hero />
      <Marquee />
      <Offer />
      <Machine />
      <Prime49 fadeUp={fadeUp} />
      <GiveHubPromo />
      <WhyUs />
      <FAQ />
      <Footer />
    </div>
  );
}

/* ---------------- Nav ---------------- */
function Nav({ menuOpen, setMenuOpen }) {
  const { d } = useLang();
  return (
    <header className="sticky top-0 z-50 border-b border-white/40 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <a href="#top" data-testid="brand-logo" className="flex items-center">
          <img src={COMPANY.logo} alt={COMPANY.legalName} className="h-9 w-auto sm:h-10" />
        </a>
        <nav className="hidden items-center gap-7 md:flex">
          {NAV_KEYS.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.key}`}
               className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {d.nav[l.key]}
            </a>
          ))}
          <LanguageSelector />
          <a href={COMPANY.phoneHref} data-testid="call-now-nav">
            <Button className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white">
              <Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}
            </Button>
          </a>
        </nav>
        <div className="flex items-center gap-3 md:hidden">
          <LanguageSelector testId="language-selector-mobile" />
          <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen((o) => !o)}>
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="border-t border-border bg-white px-6 py-4 md:hidden">
          {NAV_KEYS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
               className="block py-2 text-base font-medium text-foreground">{d.nav[l.key]}</a>
          ))}
          <a href={COMPANY.phoneHref} data-testid="call-now-mobile">
            <Button className="mt-2 w-full rounded-full bg-primary font-semibold text-primary-foreground">
              <Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}
            </Button>
          </a>
        </div>
      )}
    </header>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  const { d } = useLang();
  const h = d.hero;
  return (
    <section id="top" className="relative overflow-hidden bg-slate-950">
      <div
        className="absolute inset-0 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(https://i.ytimg.com/vi/${YT_ID}/maxresdefault.jpg)` }}
      >
        <iframe
          title="PayNoMoreFees.com background video"
          data-testid="hero-bg-video"
          className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2"
          src={`https://www.youtube-nocookie.com/embed/${YT_ID}?autoplay=1&mute=1&loop=1&playlist=${YT_ID}&controls=0&showinfo=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&iv_load_policy=3`}
          allow="autoplay; encrypted-media"
          frameBorder="0"
          allowFullScreen
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/65 to-slate-950/35" />
      <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:px-12 lg:grid-cols-2 lg:py-24">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/20 px-4 py-1.5 text-sm font-semibold text-accent">
            {h.badge}
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {h.titleBefore}<span className="text-primary">{h.titleHighlight}</span>{h.titleAfter}
          </h1>
          <p className="mt-6 max-w-lg text-lg text-white/70">{h.subtitle}</p>
          <ul className="mt-6 grid grid-cols-2 gap-2">
            {h.noFees.map((label) => (
              <li key={label} className="flex items-center gap-2 text-sm font-medium text-white">
                <Ban className="h-4 w-4 text-primary" /> {label}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="h-5 w-5 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-sm font-medium text-white/70">{h.trusted}</p>
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#quote" data-testid="hero-cta-quote">
              <Button className="h-12 w-full rounded-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white sm:w-auto">
                {h.ctaSave} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a href={COMPANY.phoneHref} data-testid="hero-cta-call">
              <Button variant="outline" className="h-12 w-full rounded-full border-2 border-white/40 bg-transparent px-8 text-base font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                <Phone className="mr-2 h-4 w-4" /> {h.ctaCall} {COMPANY.phone}
              </Button>
            </a>
          </div>
        </motion.div>
        <motion.div id="quote" initial="hidden" animate="show" variants={fadeUp} custom={1} className="scroll-mt-24">
          <QuoteForm source="merchant" />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Marquee ---------------- */
function Marquee() {
  const { d } = useLang();
  const row = [...d.marquee.items, ...d.marquee.items];
  return (
    <section className="border-y border-border bg-white py-6">
      <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {d.marquee.heading}
      </p>
      <div className="relative overflow-hidden">
        <div className="flex w-max animate-marquee gap-12 whitespace-nowrap">
          {row.map((it, i) => (
            <span key={`${it}-${i}`} className="flex items-center gap-2 font-display text-xl font-medium text-foreground/40">
              <Store className="h-5 w-5" /> {it}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Offer (Bento) ---------------- */
function Offer() {
  const { d } = useLang();
  const o = d.offer;
  return (
    <section id="offer" className="mx-auto max-w-7xl px-6 py-20 md:px-12 lg:py-28">
      <motion.div
        initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
        className="mb-12 max-w-2xl"
      >
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">{o.tag}</p>
        <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{o.heading}</h2>
      </motion.div>
      <div className="grid grid-cols-12 gap-4">
        <motion.div
          initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
          className="col-span-12 flex flex-col justify-between rounded-3xl bg-primary p-8 text-primary-foreground md:col-span-8"
          data-testid="offer-zero-fee"
        >
          <DollarSign className="h-9 w-9" />
          <div className="mt-6">
            <h3 className="font-display text-3xl font-semibold sm:text-4xl">{o.zeroFeeTitle}</h3>
            <p className="mt-2 max-w-md text-primary-foreground/80">{o.zeroFeeDesc}</p>
          </div>
        </motion.div>

        <BentoCard testid="offer-earn" className="md:col-span-4" icon={Wallet} tone="accent">
          <h3 className="font-display text-xl font-semibold">{o.earnTitle}</h3>
          <p className="mt-2 text-muted-foreground">{o.earnDesc}</p>
        </BentoCard>

        <BentoCard testid="offer-free-machine" className="md:col-span-4" icon={CreditCard}>
          <h3 className="font-display text-xl font-semibold">{o.freeTitle}</h3>
          <p className="mt-2 text-muted-foreground">{o.freeDesc}</p>
        </BentoCard>

        <BentoCard testid="offer-fast" className="md:col-span-4" icon={Truck}>
          <h3 className="font-display text-xl font-semibold">{o.fastTitle}</h3>
          <p className="mt-2 text-muted-foreground">{o.fastDesc}</p>
        </BentoCard>

        <BentoCard testid="offer-anywhere" className="md:col-span-4" icon={Smartphone}>
          <h3 className="font-display text-xl font-semibold">{o.anywhereTitle}</h3>
          <p className="mt-2 text-muted-foreground">{o.anywhereDesc}</p>
        </BentoCard>

        <motion.div
          initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
          className="col-span-12 grid grid-cols-2 gap-4 rounded-3xl border border-border bg-secondary p-8 md:col-span-8 md:grid-cols-4"
          data-testid="offer-no-fees"
        >
          {d.hero.noFees.map((label) => (
            <div key={label} className="flex flex-col items-start">
              <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Ban className="h-5 w-5" />
              </span>
              <p className="font-display text-sm font-semibold leading-tight">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function BentoCard({ children, className = "", icon: Icon, tone, testid }) {
  const accent = tone === "accent";
  return (
    <motion.div
      initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
      data-testid={testid}
      className={`col-span-12 rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${accent ? "border-accent/30 bg-accent/5" : "border-border bg-white"} ${className}`}
    >
      <span className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${accent ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"}`}>
        <Icon className="h-6 w-6" />
      </span>
      {children}
    </motion.div>
  );
}

/* ---------------- Machine ---------------- */
function Machine() {
  const { d } = useLang();
  const m = d.machine;
  const specIcons = [Monitor, Smartphone, CreditCard, LayoutGrid];
  return (
    <section id="machine" className="bg-slate-950 py-20 text-white lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} className="relative">
          <div className="overflow-hidden rounded-3xl shadow-2xl">
            <img src={MACHINE_IMG} alt="Modern payment terminal accepting a card" data-testid="machine-image"
                 className="h-full w-full object-cover" />
          </div>
          <span className="absolute -bottom-4 left-6 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg">
            {m.badge}
          </span>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={1}>
          <p className="text-sm font-semibold uppercase tracking-widest text-accent">{m.tag}</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{m.heading}</h2>
          <p className="mt-4 max-w-md text-lg text-white/60">{m.desc}</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {m.specs.map((s, i) => {
              const Icon = specIcons[i];
              return (
                <div key={s.label} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-semibold">{s.label}</p>
                    <p className="text-sm text-white/50">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <a href="#quote" data-testid="machine-cta-quote">
            <Button className="mt-8 h-12 rounded-full bg-white px-8 text-base font-semibold text-slate-950 hover:bg-white/90">
              {m.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Why Us ---------------- */
function WhyUs() {
  const { d } = useLang();
  const w = d.why;
  return (
    <section id="why" className="bg-secondary py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}>
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">{w.tag} {COMPANY.shortName}</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{w.heading}</h2>
          <p className="mt-4 text-lg text-muted-foreground">{w.desc}</p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {w.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-foreground">{p}</span>
              </li>
            ))}
          </ul>
          <a href="#quote" data-testid="why-cta-quote">
            <Button className="mt-8 h-12 rounded-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white">
              {w.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </motion.div>
        <motion.div
          initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={1}
          className="relative overflow-hidden rounded-3xl shadow-2xl"
        >
          <img src={CHECKOUT_BG} alt="Retail checkout" className="h-full w-full object-cover" />
          <div className="absolute bottom-6 left-6 rounded-2xl bg-white/95 p-5 shadow-xl backdrop-blur">
            <p className="font-display text-3xl font-semibold text-primary">100%</p>
            <p className="text-sm text-muted-foreground">{w.statLabel}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FAQ() {
  const { d } = useLang();
  return (
    <section id="faq" className="mx-auto max-w-3xl px-6 py-20 md:px-12 lg:py-28">
      <motion.div
        initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
        className="mb-10 text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">{d.faq.tag}</p>
        <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{d.faq.heading}</h2>
      </motion.div>
      <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
        {d.faq.items.map((f, i) => (
          <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-display text-lg font-medium">{f.q}</AccordionTrigger>
            <AccordionContent className="text-base text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

/* ---------------- GiveHub Promo ---------------- */
function GiveHubPromo() {
  const { d } = useLang();
  const g = d.ghp;
  const audienceIcons = [Building2, Church, HeartHandshake, GraduationCap];
  return (
    <section id="givehub" className="relative overflow-hidden bg-slate-950 py-20 text-white lg:py-28">
      <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}>
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/15 px-4 py-1.5 text-sm font-semibold text-accent">
            <HandCoins className="h-4 w-4" /> {g.tag}
          </span>
          <p className="mt-6 font-display text-2xl font-semibold tracking-tight text-primary">GiveHub</p>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{g.title}</h2>
          <p className="mt-4 max-w-lg text-lg text-white/70">{g.desc}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {g.audiences.map((a, i) => {
              const Icon = audienceIcons[i] || HandCoins;
              return (
                <span key={a} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-accent" /> {a}
                </span>
              );
            })}
          </div>
          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Link to="/givehub" data-testid="givehub-promo-cta">
              <Button className="h-12 rounded-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white">
                {g.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <span className="text-sm font-semibold text-accent">{g.price}</span>
          </div>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={1}>
          <div className="grid grid-cols-2 gap-4">
            {[
              { t: "Donations & Tithes", d: "Secure online giving, recurring gifts & text-to-give." },
              { t: "Events & Auctions", d: "Silent auctions, raffles, ticketing & more." },
              { t: "AI Insights", d: "Real-time dashboards & smart reports." },
              { t: "Zero Fees", d: "No credit card, transaction, swipe or deposit fees." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="font-display text-lg font-semibold">{c.t}</p>
                <p className="mt-1 text-sm text-white/55">{c.d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  const { d } = useLang();
  const ft = d.footer;
  return (
    <footer className="bg-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">{ft.heading}</h2>
            <p className="mt-4 max-w-md text-lg text-white/60">{ft.desc}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href={COMPANY.phoneHref} data-testid="footer-cta-call">
                <Button className="h-14 w-full rounded-full bg-white px-8 text-lg font-bold text-slate-950 hover:bg-white/90 sm:w-auto">
                  <Phone className="mr-2 h-5 w-5" /> {COMPANY.phone}
                </Button>
              </a>
              <a href="#quote" data-testid="footer-cta-quote">
                <Button variant="outline" className="h-14 w-full rounded-full border-2 border-white/30 bg-transparent px-8 text-lg font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                  {ft.ctaQuote}
                </Button>
              </a>
            </div>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:justify-items-end">
            <div>
              <div className="mb-4 inline-flex rounded-xl bg-white p-3 shadow-lg">
                <img src={COMPANY.logo} alt={COMPANY.legalName} data-testid="footer-logo" className="h-9 w-auto" />
              </div>
              <p className="text-sm text-white/50">{ft.companyDesc}</p>
            </div>
            <div className="space-y-2 text-sm text-white/60">
              <p className="font-semibold text-white">{ft.contact}</p>
              <a href={COMPANY.phoneHref} className="block hover:text-white" data-testid="footer-phone">{COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`} className="block hover:text-white" data-testid="footer-email">{COMPANY.email}</a>
              <a href={COMPANY.websiteUrl} target="_blank" rel="noopener noreferrer" className="block font-medium text-primary hover:text-white" data-testid="footer-website">www.paynomorefees.com</a>
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/40 sm:flex-row">
          <p>© {new Date().getFullYear()} {COMPANY.name}. {ft.rights}</p>
          <a href="/admin" data-testid="footer-admin-link" className="hover:text-white/70">{ft.admin}</a>
        </div>
      </div>
    </footer>
  );
}
